import { useState, useCallback, useEffect } from "react";
import {
  computeEffectiveSplitMode,
  getNextSplitPageState,
  getPrevSplitPageState,
  SplitMode,
  SplitPageState,
} from "../../../utils/splitPageUtils";
import { ChordsRow } from "../../../Entities/SongModel";

export const OVERLAP_HEIGHT = 42; // Overlap pixel height (approx 1.5 lines) for split view page boundaries

interface UseSongPaginationProps {
  body?: ChordsRow[];
  paneRefs: React.MutableRefObject<(HTMLDivElement | null)[]>;
  lyricsRef: React.MutableRefObject<HTMLDivElement | null>;
  fontChange: number;
  isFullscreen: boolean;
  scrollOffsetsRef: React.MutableRefObject<number[]>;
  baseScrollPosRef: React.MutableRefObject<number>;
  unifiedYRef?: React.MutableRefObject<number>;
}

export function useSongPagination({
  body,
  paneRefs,
  lyricsRef,
  fontChange,
  isFullscreen,
  scrollOffsetsRef,
  baseScrollPosRef,
  unifiedYRef,
}: UseSongPaginationProps) {
  const [preferredSplitMode, setPreferredSplitMode] = useState<SplitMode>(1);
  const [effectiveSplitMode, setEffectiveSplitMode] = useState<SplitMode>(1);
  const [splitPageState, setSplitPageState] = useState<SplitPageState>({
    pages: [1],
    activeTurnIndex: 0,
  });
  const [totalPages, setTotalPages] = useState<number>(1);

  // Update totalPages based on pane height / scrollHeight
  const updateTotalPages = useCallback(() => {
    const primaryPane = paneRefs.current[0] || lyricsRef.current;
    if (primaryPane) {
      const clientH = primaryPane.clientHeight;
      const scrollH = primaryPane.scrollHeight;
      if (clientH > 0) {
        const pagesCount = Math.max(1, Math.ceil(scrollH / clientH));
        setTotalPages(pagesCount);
        return;
      }
    }
    // Fallback based on rows count
    const rowsCount = body?.length || 0;
    setTotalPages(Math.max(1, Math.ceil(rowsCount / 12)));
  }, [body, paneRefs, lyricsRef]);

  // Calculate effective split mode based on content fit
  const calculateEffectiveMode = useCallback((): SplitMode => {
    if (preferredSplitMode <= 1) return 1;

    const primaryPane = paneRefs.current[0] || lyricsRef.current;
    if (primaryPane && primaryPane.clientHeight > 0 && primaryPane.scrollHeight > 0) {
      return computeEffectiveSplitMode(
        preferredSplitMode,
        primaryPane.scrollHeight,
        primaryPane.clientHeight,
        OVERLAP_HEIGHT
      );
    }
    return preferredSplitMode;
  }, [preferredSplitMode, paneRefs, lyricsRef]);

  // Reactively update effectiveSplitMode when layout, font size, body, or preferredSplitMode change
  useEffect(() => {
    const nextMode = calculateEffectiveMode();
    setEffectiveSplitMode((prev) => {
      if (prev !== nextMode) {
        setSplitPageState((prevState) => {
          const pages: number[] = [];
          for (let i = 0; i < nextMode; i++) {
            pages.push(prevState.pages[i] ?? (i + 1));
          }
          return {
            pages,
            activeTurnIndex: 0,
          };
        });
        return nextMode;
      }
      return prev;
    });
  }, [calculateEffectiveMode, fontChange, body, isFullscreen]);

  // Handle split mode change by user
  const handleSplitModeChange = useCallback((
    _event: React.MouseEvent<HTMLElement> | null,
    newMode: SplitMode | null
  ) => {
    if (!newMode) return;
    setPreferredSplitMode(newMode);

    const primaryPane = paneRefs.current[0] || lyricsRef.current;
    let effective = newMode;
    if (primaryPane && primaryPane.clientHeight > 0 && primaryPane.scrollHeight > 0) {
      effective = computeEffectiveSplitMode(
        newMode,
        primaryPane.scrollHeight,
        primaryPane.clientHeight,
        OVERLAP_HEIGHT
      );
    }
    setEffectiveSplitMode(effective);

    const initialPages: number[] = [];
    for (let i = 0; i < effective; i++) {
      initialPages.push(i + 1);
    }
    setSplitPageState({
      pages: initialPages,
      activeTurnIndex: 0,
    });
  }, [paneRefs, lyricsRef]);

  // Turn page forward (Next: '.')
  const handleNextPage = useCallback(() => {
    setSplitPageState((prev) => getNextSplitPageState(prev, effectiveSplitMode, totalPages));
  }, [effectiveSplitMode, totalPages]);

  // Turn page back (Prev: ',')
  const handlePrevPage = useCallback(() => {
    setSplitPageState((prev) => getPrevSplitPageState(prev, effectiveSplitMode, totalPages));
  }, [effectiveSplitMode, totalPages]);

  // Calculate target offset for a pane based on page state
  const computePaneBaseOffset = useCallback((pageNumber: number, paneEl: HTMLElement | null) => {
    if (!paneEl) return 0;
    if (pageNumber <= 1) return 0;
    const pageHeight = paneEl.clientHeight;
    const effectiveStep = Math.max(1, pageHeight - OVERLAP_HEIGHT);
    const rawOffset = (pageNumber - 1) * effectiveStep;
    const maxScroll = Math.max(0, paneEl.scrollHeight - paneEl.clientHeight);
    return Math.max(0, Math.min(rawOffset, maxScroll));
  }, []);

  // Sync pane scroll positions and offsets
  const syncPaneOffsets = useCallback(() => {
    const offsets: number[] = [];
    paneRefs.current.forEach((paneEl, index) => {
      if (!paneEl) return;
      const targetPage = splitPageState.pages[index] ?? (index + 1);
      const targetScrollTop = computePaneBaseOffset(targetPage, paneEl);
      offsets[index] = targetScrollTop;
      paneEl.scrollTo({
        top: targetScrollTop,
        behavior: "smooth",
      });
    });
    scrollOffsetsRef.current = offsets;
    baseScrollPosRef.current = offsets[0] || 0;
    if (unifiedYRef) {
      unifiedYRef.current = 0;
    }
  }, [splitPageState, computePaneBaseOffset, paneRefs, scrollOffsetsRef, baseScrollPosRef, unifiedYRef]);

  // Update pane scroll positions when splitPageState or fontChange changes
  useEffect(() => {
    syncPaneOffsets();
  }, [splitPageState, fontChange, syncPaneOffsets]);

  // Recalculate totalPages, effective mode and sync offsets on environment changes
  useEffect(() => {
    updateTotalPages();
    const nextMode = calculateEffectiveMode();
    if (nextMode !== effectiveSplitMode) {
      setEffectiveSplitMode(nextMode);
    }
    syncPaneOffsets();

    const timer = setTimeout(() => {
      updateTotalPages();
      const delayedMode = calculateEffectiveMode();
      if (delayedMode !== effectiveSplitMode) {
        setEffectiveSplitMode(delayedMode);
      }
      syncPaneOffsets();
    }, 50);

    const handleResize = () => {
      updateTotalPages();
      const resizeMode = calculateEffectiveMode();
      if (resizeMode !== effectiveSplitMode) {
        setEffectiveSplitMode(resizeMode);
      }
      syncPaneOffsets();
    };

    window.addEventListener("resize", handleResize);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", handleResize);
    };
  }, [body, fontChange, preferredSplitMode, effectiveSplitMode, isFullscreen, updateTotalPages, calculateEffectiveMode, syncPaneOffsets]);

  return {
    splitMode: effectiveSplitMode,
    preferredSplitMode,
    effectiveSplitMode,
    setSplitMode: handleSplitModeChange,
    splitPageState,
    setSplitPageState,
    totalPages,
    handleSplitModeChange,
    handleNextPage,
    handlePrevPage,
    computePaneBaseOffset,
    updateTotalPages,
  };
}
